/**
 * MorphoHelper — Calculs de tailles vestimentaires depuis taille/poids
 *
 * Source des barèmes : ASOS, Zalando size guides 2024-2025 (FR/UE).
 * Tolérance ±1 taille car la confection varie selon les marques.
 */

/**
 * Calcule l'IMC d'un user (Indice de Masse Corporelle).
 * IMC = poids(kg) / taille(m)^2
 */
export function computeBMI(heightCm, weightKg) {
  if (!heightCm || !weightKg) return null;
  const m = heightCm / 100;
  return weightKg / (m * m);
}

/**
 * Renvoie une silhouette suggérée à partir de l'IMC.
 * Sert juste de pré-remplissage initial — l'user peut toujours corriger.
 */
export function suggestSilhouetteFromBMI(heightCm, weightKg, gender) {
  const bmi = computeBMI(heightCm, weightKg);
  if (!bmi) return null;
  if (bmi < 18.5) return 'mince';
  if (bmi < 23)   return gender === 'M' ? 'athletique' : 'mince';
  if (bmi < 27)   return 'normal';
  if (bmi < 31)   return 'costaud';
  return 'forme';
}

/**
 * Estime les tailles vestimentaires depuis la morpho.
 * Retourne un objet { tops, bottoms, shoes, jacket } avec valeurs UE.
 *
 * Utilisé pour pré-remplir l'écran d'onboarding — l'user ajuste ensuite.
 */
export function estimateSizes(heightCm, weightKg, gender) {
  if (!heightCm || !weightKg) return {};

  const sizes = {};

  if (gender === 'M') {
    // ─ HOMMES ─
    // Hauts (S/M/L/XL/XXL) : déterminé par poids principalement
    if      (weightKg < 60)  sizes.tops = 'S';
    else if (weightKg < 75)  sizes.tops = 'M';
    else if (weightKg < 90)  sizes.tops = 'L';
    else if (weightKg < 105) sizes.tops = 'XL';
    else                     sizes.tops = 'XXL';

    // Bas (UE FR) : correspondance approximative avec le tour de taille
    //   38=tour 76cm, 40=80, 42=84, 44=88, 46=92, 48=96, 50=100, 52=104
    // Estimation tour de taille (cm) ≈ poids * 0.85 + (taille - 170) * 0.2 + 16
    const waistCm = Math.round(weightKg * 0.85 + (heightCm - 170) * 0.2 + 16);
    // Conversion en taille UE FR : (waistCm - 76) / 2 + 38, arrondi au pair supérieur
    let bottomUE = Math.round((waistCm - 76) / 2 + 38);
    if (bottomUE % 2 !== 0) bottomUE += 1; // forcer parité
    sizes.bottoms = String(Math.max(38, Math.min(54, bottomUE)));

    // Veste = même que hauts
    sizes.jacket = sizes.tops;

    // Chaussures (UE) : taille corrélée à la stature
    if      (heightCm < 165) sizes.shoes = '40';
    else if (heightCm < 172) sizes.shoes = '41';
    else if (heightCm < 178) sizes.shoes = '42';
    else if (heightCm < 184) sizes.shoes = '43';
    else if (heightCm < 190) sizes.shoes = '44';
    else                     sizes.shoes = '45';

  } else {
    // ─ FEMMES ─
    // Hauts (XS/S/M/L/XL) : déterminé par poids + taille
    if      (weightKg < 50)  sizes.tops = 'XS';
    else if (weightKg < 60)  sizes.tops = 'S';
    else if (weightKg < 70)  sizes.tops = 'M';
    else if (weightKg < 80)  sizes.tops = 'L';
    else                     sizes.tops = 'XL';

    // Bas (UE 34/36/38/40/42/44) : poids principal
    if      (weightKg < 50)  sizes.bottoms = '34';
    else if (weightKg < 56)  sizes.bottoms = '36';
    else if (weightKg < 62)  sizes.bottoms = '38';
    else if (weightKg < 70)  sizes.bottoms = '40';
    else if (weightKg < 80)  sizes.bottoms = '42';
    else                     sizes.bottoms = '44';

    // Veste : même que hauts (un peu ajustable)
    sizes.jacket = sizes.tops;

    // Chaussures (UE) : taille corrélée à la stature
    if      (heightCm < 155) sizes.shoes = '36';
    else if (heightCm < 160) sizes.shoes = '37';
    else if (heightCm < 165) sizes.shoes = '38';
    else if (heightCm < 170) sizes.shoes = '39';
    else if (heightCm < 175) sizes.shoes = '40';
    else                     sizes.shoes = '41';
  }

  return sizes;
}

/**
 * Renvoie le label affichable d'une silhouette selon le genre.
 *
 * Pour les hommes : mots simples (mince, athletique, normal, costaud, forme)
 * Pour les femmes : morphologies classiques en mode/stylisme
 *   - sablier  : épaules ≈ hanches, taille marquée
 *   - poire    : hanches > épaules
 *   - pomme    : ventre marqué, jambes fines
 *   - rectangle: épaules ≈ taille ≈ hanches (peu de courbes)
 *   - triangle_inv : épaules > hanches
 *   - mince/forme : pour des cas généraux
 */
export function silhouetteLabel(code, gender) {
  const labels = {
    'M': {
      mince:        'Élancé',
      athletique:   'Sportif',
      normal:       'Équilibré',
      costaud:      'Robuste',
      forme:        'Carré',
    },
    'F': {
      sablier:       'Sablier',
      poire:         'Trapèze',
      pomme:         'Ovale',
      rectangle:     'Allongée',
      triangle_inv:  'Triangle',
    },
  };
  const set = labels[gender] || labels['F'];
  return set[code] || code;
}

/**
 * Liste des options de silhouette pour un genre.
 *
 * v1.12.277 — Accepte un translator `t` optionnel pour i18n des labels.
 * Si `t` est fourni, les labels passent par les clés `silhouette_*`.
 * Sinon, fallback sur les labels FR (compatibilité ancien code).
 */
export function silhouetteOptions(gender, t) {
  const tr = (key, fallback) => (t ? t(key) : fallback);
  if (gender === 'M') {
    return [
      { value: 'mince',      label: tr('silhouette_v', 'V (athlétique)'),         hint: tr('sil_hint_slim_m', 'Carrure fine, peu de muscles apparents') },
      { value: 'athletique', label: tr('silhouette_v', 'V (athlétique)'),         hint: tr('sil_hint_athletic_m', 'Carrure marquée, musculature visible') },
      { value: 'normal',     label: tr('silhouette_rectangle_m', 'Rectangle'),    hint: tr('sil_hint_average_m', 'Carrure moyenne, proportions classiques') },
      { value: 'costaud',    label: tr('silhouette_trapezoidal', 'Trapézoïdal'),  hint: tr('sil_hint_broad_m', 'Carrure large, ossature forte') },
      { value: 'forme',      label: tr('silhouette_oval', 'Ovale'),               hint: tr('sil_hint_full_m', 'Buste imposant, ventre marqué') },
    ];
  }
  // Femmes (et 'N' Autre)
  return [
    { value: 'sablier',      label: tr('silhouette_hourglass', 'Sablier'),                  hint: tr('sil_hint_hourglass', 'Épaules ≈ hanches, taille marquée') },
    { value: 'poire',        label: tr('silhouette_pear', 'Poire'),                         hint: tr('sil_hint_pear', 'Hanches plus larges que les épaules') },
    { value: 'rectangle',    label: tr('silhouette_rectangle', 'Rectangle'),                hint: tr('sil_hint_rectangle', 'Peu de courbes, taille peu marquée') },
    { value: 'pomme',        label: tr('silhouette_apple', 'Pomme'),                        hint: tr('sil_hint_apple', 'Buste/ventre généreux, jambes fines') },
    { value: 'triangle_inv', label: tr('silhouette_inverted_triangle', 'Triangle inversé'), hint: tr('sil_hint_inverted_triangle', 'Épaules plus larges que les hanches') },
  ];
}

/**
 * Liste des tailles disponibles selon le type et le genre.
 */
export function sizeOptions(type, gender) {
  if (type === 'shoes') {
    if (gender === 'M') return ['39','40','41','42','43','44','45','46','47'];
    return ['35','36','37','38','39','40','41','42'];
  }
  if (type === 'tops' || type === 'jacket') {
    return ['XS','S','M','L','XL','XXL'];
  }
  if (type === 'bottoms') {
    if (gender === 'M') {
      // Tailles UE FR (38=XS, 40=S, 42=M, 44=L, 46=XL, 48=XXL)
      return ['38','40','42','44','46','48','50','52'];
    }
    return ['34','36','38','40','42','44','46'];
  }
  return [];
}

export default {
  computeBMI,
  suggestSilhouetteFromBMI,
  estimateSizes,
  silhouetteLabel,
  silhouetteOptions,
  sizeOptions,
};
